import { GEN1_STATUS, isGen1Status } from '../Components/Gen1/gen1Status';

/** Save 押下で送る Status: 常に Draft */
export function getGen1SavePayloadStatus() {
    return GEN1_STATUS.DRAFT;
}

/**
 * Confirm / プレビュー確定で送る Status
 * Draft → Ordered。Ordered / In Progress は維持。
 */
export function getGen1ConfirmSubmitPayloadStatus(gen1Order) {
    const status = gen1Order?.Status;
    if (!isGen1Status(status)) return GEN1_STATUS.ORDERED;
    if (status === GEN1_STATUS.DRAFT) return GEN1_STATUS.ORDERED;
    return status;
}

/**
 * フォーム state から API/DB 保存用の payload を整形して構築する。
 * 保存時・送信時に共通で使用する。DB/ViewModel のプロパティ名に揃える。
 * @param {Object} state - Gen1Application の state（gen1Order, orderNumber, otherSystemId 等）
 * @param {{ SYSTEM_TYPE_NEW: string, SYSTEM_ID_FOR_NEW: string, status?: string }} deps - status: Gen1TrunkingOrderHeader.Status。`getGen1SavePayloadStatus` / `getGen1ConfirmSubmitPayloadStatus` と組み合わせる。
 * @returns {{ Gen1TrunkingOrderHeader: Object, Gen1TrunkingOrderItem: Object, Gen1TrunkingOrderRepeaterSerials: Object[] }}
 */
export function buildGen1OrderPayload(state, deps) {
    const { SYSTEM_TYPE_NEW, SYSTEM_ID_FOR_NEW, status } = deps ?? {};
    const order = state?.gen1Order ?? {};
    const item = order.Gen1TrunkingOrderItem ?? {};
    const repeaterSerials = order.Gen1TrunkingOrderRepeaterSerials ?? [];

    const includePurchasedRepeaters = order.IncludePurchasedRepeaters === true;
    const systemId =
        order.SystemType === SYSTEM_TYPE_NEW
            ? SYSTEM_ID_FOR_NEW
            : (order.SystemId ?? '').trim();

    // OrderNumber / OtherSystemId は ViewModel にないため Header には含めない。OrderNumber は Search 由来の Serial 行にのみ、OtherSystemId は Exchange Add 行の ExchangeSystemId として持つ。
    const Gen1TrunkingOrderHeader = {
        // 更新時に backend 側で特定できるよう id を引き継ぐ（存在する場合のみ）
        ...(order.id != null &&
            order.id !== 0 &&
            order.id !== '' && { id: order.id }),
        ...(status != null && status !== '' && { Status: status }),
        ContactName: (order.ContactName ?? '').trim(),
        ContactEmail: (order.ContactEmail ?? '').trim(),
        AddressLineOne: (order.AddressLineOne ?? '').trim(),
        AddressLineTwo: (order.AddressLineTwo ?? '').trim(),
        AddressLineThree: (order.AddressLineThree ?? '').trim(),
        City: (order.City ?? '').trim(),
        State: order.State ?? '',
        Zip: (order.Zip ?? '').trim(),
        Country: (order.Country ?? '').trim(),
        Telephone: (order.Telephone ?? '').trim(),
        ManufactureRep: (order.ManufactureRep ?? '').trim(),
        SystemType: order.SystemType ?? '',
        SystemId: systemId,
        DealerOrderNumber: (order.DealerOrderNumber ?? '').trim(),
        IncludePurchasedRepeaters: includePurchasedRepeaters,
        AdditionalNotes: (order.AdditionalNotes ?? '').trim(),
    };

    const Gen1TrunkingOrderItem = {
        ...(item.id != null &&
            item.id !== 0 &&
            item.id !== '' && { id: item.id }),
        RepeaterQty: Number(item.RepeaterQty) || 0,
        MultiSiteNetworkingQty: Number(item.MultiSiteNetworkingQty) || 0,
        ExchangeRepeaterPairQty: Number(item.ExchangeRepeaterPairQty) || 0,
        RemoveRepeaterQty: Number(item.RemoveRepeaterQty) || 0,
        isNeededUsbKeyAndSoftware: item.isNeededUsbKeyAndSoftware === true,
        Grade1UnlimitedAccessUsbKeyQty:
            Number(item.Grade1UnlimitedAccessUsbKeyQty) || 0,
        Grade2LimitedAccessUsbKeyQty:
            Number(item.Grade2LimitedAccessUsbKeyQty) || 0,
        SystemManagerSoftwareWithGrade1KeyQty:
            Number(item.SystemManagerSoftwareWithGrade1KeyQty) || 0,
        SystemManagerSoftwareQty: Number(item.SystemManagerSoftwareQty) || 0,
    };

    const Gen1TrunkingOrderRepeaterSerials = repeaterSerials.map((r, idx) => ({
        ...(r.id != null && r.id !== 0 && r.id !== '' && { id: r.id }),
        // ExchangePairId は任意（Exchange 時のみ設定される）
        ...(r.ExchangePairId != null &&
            r.ExchangePairId !== '' && {
                ExchangePairId: r.ExchangePairId,
            }),
        OrderNumber: (r.OrderNumber ?? '').trim(),
        OperationType: (r.OperationType ?? '').trim(),
        ExchangeSystemId: (r.ExchangeSystemId ?? '').trim(),
        BaseModel: (r.BaseModel ?? '').trim(),
        SerialNumber: (r.SerialNumber ?? '').trim(),
        LineSequenceNumber: Number(r.LineSequenceNumber) || idx + 1,
    }));

    return {
        ...(order.id != null &&
            order.id !== 0 &&
            order.id !== '' &&
            Number.isFinite(Number(order.Revision)) &&
            Number(order.Revision) > 0 && {
                expectedHeaderRevision: Number(order.Revision),
            }),
        Gen1TrunkingOrderHeader,
        Gen1TrunkingOrderItem,
        Gen1TrunkingOrderRepeaterSerials,
        isSaveAddressBook: state?.isSaveAddressBook === true,
    };
}
